import React, { useState } from 'react';
import { ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';

export type SortDirection = 'asc' | 'desc' | null;

export interface TableColumn<T> {
  key: string;
  label: string;
  render?: (row: T, index: number) => React.ReactNode;
  className?: string;
  sortable?: boolean;
  sortValue?: (row: T) => any; // Custom value accessor for sorting
}

interface DataTableProps<T> {
  columns: TableColumn<T>[];
  data: T[];
  emptyMessage?: string;
  striped?: boolean;
  getRowKey?: (row: T, index: number) => string | number;
  defaultSortKey?: string;
  defaultSortDirection?: SortDirection;
  onRowClick?: (row: T, index: number) => void;
}

function DataTable<T extends Record<string, any>>({
  columns,
  data,
  emptyMessage = 'No data available',
  striped = true,
  getRowKey,
  defaultSortKey,
  defaultSortDirection = null,
  onRowClick
}: DataTableProps<T>) {
  const [sortKey, setSortKey] = useState<string | null>(defaultSortKey || null);
  const [sortDirection, setSortDirection] = useState<SortDirection>(defaultSortDirection);

  const handleSort = (column: TableColumn<T>) => {
    if (!column.sortable) return;

    if (sortKey === column.key) {
      // Cycle through: asc -> desc -> null
      if (sortDirection === 'asc') {
        setSortDirection('desc');
      } else if (sortDirection === 'desc') {
        setSortDirection(null);
        setSortKey(null);
      }
    } else {
      setSortKey(column.key);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (column: TableColumn<T>) => {
    if (!column.sortable) return null;

    if (sortKey === column.key) {
      return sortDirection === 'asc' ? (
        <ArrowUp className="inline w-4 h-4 ml-1" />
      ) : (
        <ArrowDown className="inline w-4 h-4 ml-1" />
      );
    }
    return <ArrowUpDown className="inline w-4 h-4 ml-1 opacity-30" />;
  };

  // Sort data if sortKey and sortDirection are set
  const sortedData = React.useMemo(() => {
    if (!sortKey || !sortDirection) return data;

    const column = columns.find(col => col.key === sortKey);
    if (!column) return data;

    return [...data].sort((a, b) => {
      let aVal = column.sortValue ? column.sortValue(a) : a[sortKey];
      let bVal = column.sortValue ? column.sortValue(b) : b[sortKey];

      // Handle null/undefined values
      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return 1;
      if (bVal == null) return -1;

      // Handle string comparison (case-insensitive)
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      }

      const comparison = aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [data, sortKey, sortDirection, columns]);

  if (data.length === 0) {
    return (
      <p className="text-sm text-gray-600 dark:text-gray-400 text-center py-4">
        {emptyMessage}
      </p>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse">
        <thead>
          <tr className="border-b-2 border-gray-300 dark:border-gray-600 text-left">
            {columns.map((column, colIndex) => (
              <th 
                key={column.key} 
                className={`p-3 text-gray-700 dark:text-gray-300 ${colIndex === 0 ? 'sticky left-0 z-10 bg-white dark:bg-gray-800 shadow-[2px_0_4px_rgba(0,0,0,0.05)] dark:shadow-[2px_0_4px_rgba(0,0,0,0.2)]' : ''} ${column.sortable ? 'cursor-pointer select-none hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors' : ''} ${column.className || ''}`}
                onClick={() => handleSort(column)}
              >
                <div className="flex items-center">
                  {column.label}
                  {getSortIcon(column)}
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sortedData.map((row, index) => {
            const rowKey = getRowKey ? getRowKey(row, index) : index;
            const bgClass = striped && index % 2 === 0 
              ? 'bg-white dark:bg-gray-800' 
              : 'bg-gray-50 dark:bg-gray-800/50';
            
            return (
              <tr
                key={rowKey}
                onClick={
                  onRowClick
                    ? (e) => {
                        // Don't trigger row navigation when an action control was clicked
                        if ((e.target as HTMLElement).closest('button, a, input, select, label')) return;
                        onRowClick(row, index);
                      }
                    : undefined
                }
                className={`group border-b border-gray-200 dark:border-gray-700 ${bgClass} ${onRowClick ? 'cursor-pointer transition-colors hover:bg-primary-50 dark:hover:bg-gray-700/50' : ''}`}
              >
                {columns.map((column, colIndex) => (
                  <td
                    key={`${rowKey}-${column.key}`}
                    className={`p-3 ${colIndex === 0 ? 'sticky left-0 z-10 shadow-[2px_0_4px_rgba(0,0,0,0.05)] dark:shadow-[2px_0_4px_rgba(0,0,0,0.2)]' : ''} ${column.className || ''} ${colIndex === 0 ? bgClass : ''} ${colIndex === 0 && onRowClick ? 'group-hover:bg-primary-50 dark:group-hover:bg-gray-700/50' : ''}`}
                  >
                    {column.render ? column.render(row, index) : row[column.key]}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default DataTable;
